import { Link } from "react-router-dom";
import { ArrowRight, Cpu, FlaskConical, Eye, ShieldCheck, Landmark, KeyRound } from "lucide-react";
import Seo from "../components/Seo";
import { Reveal, Stagger, StaggerItem } from "../components/Reveal";
import { PageHero, CTABand } from "../components/PageParts";
import SectionHeading from "../components/SectionHeading";

const PILLARS = [
  {
    icon: Cpu,
    title: "Technology-driven trading approaches",
    text: "We build systematic, rules-based trading approaches on quantitative research. Discretion is bounded by process; every model operates within defined parameters that are documented and reviewable.",
  },
  {
    icon: FlaskConical,
    title: "Systematic strategy research",
    text: "Strategies pass through a research pipeline — hypothesis, backtest, stress test, forward test — before they are ever presented. Each stage has its own label, and the labels are never mixed.",
  },
  {
    icon: Eye,
    title: "Transparent operating models",
    text: "Every offering on this site states its custody arrangement, mechanics and terms plainly. When a fact depends on provider confirmation, we say so instead of estimating.",
  },
  {
    icon: ShieldCheck,
    title: "Performance reporting and verification",
    text: "Results are labelled backtested, simulated, live, audited or independently verified — with period, source and last-updated date. Unavailable data becomes a placeholder, never a number.",
  },
];

export default function About() {
  return (
    <>
      <Seo
        title="About Us — The Kalki AI Fund Approach"
        path="/about"
        description="Kalki AI Fund's approach: technology-driven trading, systematic strategy research, transparent operating models and verified performance reporting."
      />

      <PageHero
        eyebrow="About Kalki AI Fund"
        titleLines={["Systematic by method,", "transparent by principle."]}
        intro="Kalki AI Fund presents a structured approach to trading technology, strategy development, risk transparency and structured digital asset opportunities — with custody arrangements always stated in plain terms."
        badges={["Institutional approach", "Risk-aware communication"]}
      />

      <section className="py-24 lg:py-28 border-t border-gold-400/10">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <SectionHeading
            eyebrow="The Approach"
            title={<>Four commitments that shape every page of this site</>}
          />
          <Stagger className="mt-14 grid md:grid-cols-2 gap-6">
            {PILLARS.map((p, i) => (
              <StaggerItem key={p.title} className="h-full">
                <div className="glass rounded-3xl p-8 h-full hover:border-gold-400/30 transition-colors duration-500">
                  <div className="flex items-center gap-4 mb-5">
                    <span className="flex h-11 w-11 items-center justify-center rounded-full border border-gold-400/30 bg-gold-400/[0.07]">
                      <p.icon size={18} className="text-gold-300" />
                    </span>
                    <span className="font-mono text-xs text-gold-400/60">0{i + 1}</span>
                  </div>
                  <h3 className="font-display text-xl font-bold tracking-tight text-ink-50 mb-3">
                    {p.title}
                  </h3>
                  <p className="text-sm text-zinc-400 leading-relaxed">{p.text}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <section className="py-24 lg:py-28 border-t border-gold-400/10 bg-ink-800/40">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <SectionHeading
            eyebrow="Custody, Distinguished"
            title={<>Two structures. Clearly separated.</>}
            intro="We deliberately separate our offerings by custody arrangement — because the question “who holds the assets?” should be answered before any other question is asked."
          />
          <Stagger className="mt-14 grid md:grid-cols-2 gap-6">
            <StaggerItem className="h-full">
              <div className="glass-gold rounded-3xl p-8 h-full" data-testid="about-non-custodial">
                <KeyRound size={20} className="text-gold-300 mb-5" />
                <h3 className="font-display text-xl font-bold text-ink-50 mb-1">Non-custodial</h3>
                <p className="font-mono text-[10px] tracking-[0.16em] text-gold-400/80 uppercase mb-4">
                  Master Trading · Strategy Trading
                </p>
                <p className="text-sm text-zinc-400 leading-relaxed">
                  Assets remain in the participant's own exchange account, or trade through
                  trade-only API keys on that account. We never receive, hold or move participant
                  funds. The exchange remains the counterparty for trading and settlement.
                </p>
              </div>
            </StaggerItem>
            <StaggerItem className="h-full">
              <div className="glass-gold rounded-3xl p-8 h-full" data-testid="about-custodial">
                <Landmark size={20} className="text-gold-300 mb-5" />
                <h3 className="font-display text-xl font-bold text-ink-50 mb-1">Custodial</h3>
                <p className="font-mono text-[10px] tracking-[0.16em] text-gold-400/80 uppercase mb-4">
                  Vault Trading · Vault Yield
                </p>
                <p className="text-sm text-zinc-400 leading-relaxed">
                  Assets are held under a custodial arrangement with the exchange, host platform
                  or issuer. Participants bear that counterparty's custody, solvency and
                  operational risk — fully disclosed on each product page.
                </p>
              </div>
            </StaggerItem>
          </Stagger>
        </div>
      </section>

      <section className="py-24 lg:py-28 border-t border-gold-400/10">
        <div className="mx-auto max-w-5xl px-5 sm:px-8">
          <SectionHeading
            eyebrow="Responsible Risk Communication"
            title={<>What we will not do</>}
            align="center"
          />
          <Stagger className="mt-14 grid sm:grid-cols-3 gap-6">
            {[
              "We do not publish invented statistics, AUM figures or investor counts.",
              "We do not promise returns, repayment or redemption — for any product.",
              "We do not blur the line between backtested, live and verified results.",
            ].map((t, i) => (
              <StaggerItem key={i} className="h-full">
                <div className="rounded-2xl border border-gold-400/15 bg-gold-400/[0.04] p-7 h-full">
                  <p className="font-mono text-xs text-gold-400/70 mb-4">NO. 0{i + 1}</p>
                  <p className="text-sm text-zinc-300 leading-relaxed">{t}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
          <Reveal delay={0.15}>
            <p className="mt-12 text-sm text-zinc-500 leading-relaxed text-center max-w-3xl mx-auto">
              All product descriptions, custody claims, yield terms and financial promotions on
              this website are intended for review against applicable legal and regulatory
              requirements before publication.
            </p>
          </Reveal>
        </div>
      </section>

      <CTABand
        testid="about-cta"
        title="See the approach applied to each offering."
        subtitle="Compare the four models side by side — or contact the team with your questions."
        primary={{ label: "Explore Our Vaults", to: "/vault-trading" }}
        secondary={{ label: "Contact Our Team", to: "/contact" }}
      />
    </>
  );
}